/**
 * Configuration Migration Utilities
 * 
 * These utilities migrate existing configurations to fix architectural issues
 * while preserving the correct bidder parameter structure.
 * 
 * Key fixes:
 * 1. Move videoSettings from context-level to placement-level
 * 2. Consolidate mediaType to placement-level only
 * 3. PRESERVE nested bidder video parameters (they're correct!)
 */

import { validateConfiguration, validatePlacement } from '../schemas/index.js';

/**
 * Migrate a single placement to the new architecture
 */
export function migratePlacement(placement) {
  if (!placement) return placement;

  const migrated = {
    selector: placement.selector
  };

  // FIX 1: Determine single mediaType (priority: explicit > context > settings > default)
  let mediaType = placement.mediaType;
  
  if (!mediaType) {
    // Check contexts for mediaType
    const contextMediaTypes = Object.values(placement.contexts || {})
      .map(ctx => ctx.mediaType)
      .filter(Boolean);
    
    if (contextMediaTypes.length > 0) {
      // Use the most permissive mediaType found
      if (contextMediaTypes.some(mt => mt === 'both')) {
        mediaType = 'both';
      } else if (contextMediaTypes.some(mt => mt === 'video')) {
        mediaType = contextMediaTypes.includes('banner') ? 'both' : 'video';
      } else {
        mediaType = 'banner';
      }
    } else {
      // Fallback to settings mediaType or default
      mediaType = placement.settings?.mediaType || 'banner';
    }
  }
  
  migrated.mediaType = mediaType;

  // FIX 2: Consolidate videoSettings to placement level
  let placementVideoSettings = null;
  
  // Priority: settings.videoSettings > context.videoSettings > null
  if (placement.settings?.videoSettings && 
      placement.settings.videoSettings !== null &&
      typeof placement.settings.videoSettings === 'object' &&
      Object.keys(placement.settings.videoSettings).length > 0) {
    placementVideoSettings = placement.settings.videoSettings;
  } else {
    // Find first non-null videoSettings in contexts
    for (const context of Object.values(placement.contexts || {})) {
      if (context.videoSettings && 
          context.videoSettings !== null &&
          typeof context.videoSettings === 'object' &&
          Object.keys(context.videoSettings).length > 0) {
        placementVideoSettings = context.videoSettings;
        break;
      }
    }
  }
  
  if (placementVideoSettings) {
    migrated.videoSettings = placementVideoSettings;
  }

  // FIX 3: Clean up contexts (remove mediaType and videoSettings)
  migrated.contexts = {};
  
  Object.entries(placement.contexts || {}).forEach(([contextId, context]) => {
    migrated.contexts[contextId] = {
      enabled: context.enabled !== undefined ? context.enabled : true,
      sizes: context.sizes || {
        desktop: [[300, 250]],
        mobile: [[300, 250]]
      },
      gamPath: context.gamPath || '',
      
      // PRESERVE: Keep bidder params exactly as they are (including nested video)
      ...(context.bidderParams && { bidderParams: context.bidderParams })
    };
    
    // Remove mediaType and videoSettings from context (now at placement level)
    // Note: We intentionally don't copy these to the context
  });

  // FIX 4: Clean up settings (remove mediaType and videoSettings)
  migrated.settings = {
    lazyLoad: placement.settings?.lazyLoad !== undefined ? placement.settings.lazyLoad : false,
    prefetch: placement.settings?.prefetch !== undefined ? placement.settings.prefetch : true,
    refresh: placement.settings?.refresh !== undefined ? placement.settings.refresh : false,
    fluid: placement.settings?.fluid !== undefined ? placement.settings.fluid : true,
    lazyLoadMargin: placement.settings?.lazyLoadMargin || {
      desktop: '800px',
      mobile: '600px'
    }
  };

  // Copy any additional settings properties except mediaType and videoSettings
  Object.keys(placement.settings || {}).forEach(key => {
    if (key !== 'mediaType' && key !== 'videoSettings' && 
        !migrated.settings.hasOwnProperty(key)) {
      migrated.settings[key] = placement.settings[key];
    }
  });

  return migrated;
}

/**
 * Migrate a complete configuration
 */
export function migrateConfiguration(config) {
  if (!config) return config;

  const migrated = {
    site: config.site,
    bidders: config.bidders,
    ...(config.userIdModules && { userIdModules: config.userIdModules }),
    placements: {}
  };

  // Migrate each placement
  Object.entries(config.placements || {}).forEach(([placementId, placement]) => {
    try {
      migrated.placements[placementId] = migratePlacement(placement);
    } catch (error) {
      console.warn(`Failed to migrate placement ${placementId}:`, error);
      // Keep original placement if migration fails
      migrated.placements[placementId] = placement;
    }
  });

  return migrated;
}

/**
 * Check if a placement needs migration
 */
export function placementNeedsMigration(placement) {
  if (!placement) return false;

  // Check for video settings in contexts
  const hasContextVideoSettings = Object.values(placement.contexts || {})
    .some(ctx => ctx.videoSettings && ctx.videoSettings !== null);

  // Check for mediaType in contexts
  const hasContextMediaType = Object.values(placement.contexts || {})
    .some(ctx => ctx.mediaType);

  // Check for mediaType in settings (should be at placement level)
  const hasSettingsMediaType = placement.settings?.mediaType && !placement.mediaType;

  // Check for videoSettings in settings (when there are also context videoSettings)
  const hasSettingsVideoSettings = placement.settings?.videoSettings && 
    hasContextVideoSettings;

  return hasContextVideoSettings || hasContextMediaType || 
         hasSettingsMediaType || hasSettingsVideoSettings;
}

/**
 * Check if a configuration needs migration
 */
export function configurationNeedsMigration(config) {
  if (!config || !config.placements) return false;

  return Object.values(config.placements).some(placementNeedsMigration);
}

/**
 * Migrate configuration with validation
 */
export function migrateAndValidateConfiguration(config) {
  try {
    // First, migrate the configuration
    const migrated = migrateConfiguration(config);

    // Then validate the migrated result
    const validated = validateConfiguration(migrated);

    return {
      success: true,
      data: validated,
      migrated: true,
      errors: null
    };
  } catch (error) {
    console.error('Migration or validation failed:', error);
    
    // Try to validate original config
    try {
      const originalValidated = validateConfiguration(config);
      return {
        success: true,
        data: originalValidated,
        migrated: false,
        errors: null,
        migrationError: error.message
      };
    } catch (originalError) {
      return {
        success: false,
        data: null,
        migrated: false,
        errors: [{ 
          path: 'migration', 
          message: `Migration failed: ${error.message}. Original validation also failed: ${originalError.message}`, 
          code: 'MIGRATION_FAILED' 
        }]
      };
    }
  }
}

/**
 * Safely migrate a placement with fallback
 */
export function migratePlacementSafe(placement) {
  try {
    if (!placementNeedsMigration(placement)) {
      // No migration needed, just validate
      const validated = validatePlacement(placement);
      return {
        success: true,
        data: validated,
        migrated: false,
        errors: null
      };
    }

    const migrated = migratePlacement(placement);
    const validated = validatePlacement(migrated);

    return {
      success: true,
      data: validated,
      migrated: true,
      errors: null
    };
  } catch (error) {
    console.warn('Placement migration failed:', error);
    
    // Fallback: try to use original placement
    try {
      const validated = validatePlacement(placement);
      return {
        success: true,
        data: validated,
        migrated: false,
        errors: null,
        migrationError: error.message
      };
    } catch (validationError) {
      return {
        success: false,
        data: placement, // Return original even if invalid
        migrated: false,
        errors: [{ 
          path: 'placement', 
          message: `Migration and validation both failed: ${error.message}`, 
          code: 'MIGRATION_VALIDATION_FAILED' 
        }]
      };
    }
  }
}

/**
 * Get migration summary for debugging
 */
export function getMigrationSummary(config) {
  const summary = {
    needsMigration: configurationNeedsMigration(config),
    totalPlacements: Object.keys(config?.placements || {}).length,
    placementsNeedingMigration: 0,
    issues: []
  };

  Object.entries(config?.placements || {}).forEach(([placementId, placement]) => {
    if (placementNeedsMigration(placement)) {
      summary.placementsNeedingMigration++;
      
      const issues = [];
      
      if (Object.values(placement.contexts || {}).some(ctx => ctx.videoSettings)) {
        issues.push('videoSettings in contexts');
      }
      
      if (Object.values(placement.contexts || {}).some(ctx => ctx.mediaType)) {
        issues.push('mediaType in contexts');
      }
      
      if (placement.settings?.mediaType && !placement.mediaType) {
        issues.push('mediaType in settings only');
      }
      
      summary.issues.push({
        placementId,
        issues
      });
    }
  });

  return summary;
}

/**
 * Demo function to show before/after migration
 */
export function demonstrateMigration(config) {
  const original = JSON.stringify(config, null, 2);
  const migrated = migrateConfiguration(config);
  const migratedJson = JSON.stringify(migrated, null, 2);
  
  console.log('=== BEFORE MIGRATION ===');
  console.log(original);
  console.log('\n=== AFTER MIGRATION ===');
  console.log(migratedJson);
  console.log('\n=== MIGRATION SUMMARY ===');
  console.log(getMigrationSummary(config));
  
  return { original: config, migrated };
}